import React, { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

import PanelCard from '../../performance-operations/components/PanelCard';
import { Clickable, Table } from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import { Note } from '../../../components/performance/evaluation/EvaluationParts';
import { formatNum, formatSigned, metaOfContext } from '../../../components/performance/evaluation/evaluationAnalytics';
import { NS, fetchTopAreas } from '../config';

// E05 alt sol — en yaygın 10 gelişim alanı (yatay çubuk). Değer = beklentinin altında kalan TEKİL kişi (kişi bir yetkinlikte tek
// bileşik kayıttır). k altındaki yetkinlik sıralamaya hiç girmez (sırası da değer sızdırırdı) — yalnız sayısı notta. Çubuk ya da
// tablo satırı → o yetkinlikte açığı olan kayıtlar.
export default function TopAreasPanel({
  filters, screen, onOpen, onMeta,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const lang = i18n.language;
  const [table, setTable] = useState(false);
  const key = JSON.stringify([filters, screen]);
  const panel = usePanel(() => fetchTopAreas(filters, screen), [key], !!filters.periodId);
  useEffect(() => { if (panel.data) onMeta?.('topAreas', metaOfContext(panel.data)); }, [panel.data, onMeta]);
  const areas = panel.data?.areas || [];
  const hidden = panel.data?.hiddenCount || 0;
  const data = areas.map((a) => ({ ...a, label: a.name }));

  return (
    <PanelCard
      panelKey="topAreas" title={t(`${NS}.panel.topAreas`)} span={5}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={areas.length === 0}
      emptyText={t(`${NS}.topAreas.empty`)}
      tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      {table ? (
        <Table>
          <thead>
            <tr>
              <th>{t(`${NS}.col.competency`)}</th>
              <th>{t(`${NS}.topAreas.persons`)}</th>
              <th>{t(`${NS}.topAreas.share`)}</th>
              <th>{t(`${NS}.topAreas.averageGap`)}</th>
            </tr>
          </thead>
          <tbody>
            {areas.map((a) => (
              <tr key={a.competencyId} data-row={a.competencyId}>
                <td title={a.name}><Clickable onClick={() => onOpen(a.competencyId)} data-open-area={a.competencyId}>{a.name}</Clickable></td>
                <td>{`${formatNum(a.gapPersons, lang, 0)} / ${formatNum(a.comparablePersons, lang, 0)}`}</td>
                <td>{`${formatNum(a.gapSharePct, lang)}%`}</td>
                <td>{formatSigned(a.averageGap, lang)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div style={{ width: '100%', height: Math.max(160, data.length * 32) }}>
          <ResponsiveContainer>
            <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: theme.metrics.font.size.xs }} />
              <YAxis type="category" dataKey="label" width={130} tick={{ fontSize: theme.metrics.font.size.xs }} />
              <Tooltip formatter={(v, _n, item) => [`${formatNum(v, lang, 0)} / ${formatNum(item?.payload?.comparablePersons, lang, 0)}`, t(`${NS}.topAreas.persons`)]} />
              <Bar
                dataKey="gapPersons" name={t(`${NS}.topAreas.persons`)} fill={theme.colors.auxiliar.danger} cursor="pointer"
                onClick={(d) => onOpen((d.payload || d).competencyId)}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {hidden > 0 ? <Note data-note="hidden">{t(`${NS}.topAreas.hidden`, { count: hidden })}</Note> : null}
    </PanelCard>
  );
}
